import React, { useState, useEffect, useCallback } from 'react'
import LoadingOverlay from './components/LoadingOverlay'
import CriticalError from './components/CriticalError'
import { getUser } from './User'
import { loadScripts, isDebug } from './App.config'
import { SessionProvider, SessionType, initialSession } from './context/SessionContext'
import { DataContextType, DataContextProvider } from './context/DataContext'

import { UserModuleProfile, Student, TSModuleProfile, LLModuleProfile } from './interfaces/ModuleTypes'
import * as LRS from './api/LRS'
import * as LMS from './LMS'

import Main from './components/Main'
import Theme from './styles/Theme'
import { ThemeProvider, CssBaseline } from '@material-ui/core'
import { GlobalStyle } from './styles/GlobalStyle'
import { isSim, isLab } from './Reporting'

type ReportingSession = SessionType
type DataContext = DataContextType

const App: React.FC = () => {
    const [scriptsLoaded, setScriptsLoaded] = useState(false)
    const [session, setSession] = useState<ReportingSession>(initialSession)
    const [reportingData, setReportingData] = useState<DataContext>()

    const handleUnload = useCallback(() => {
        const { user, userProfile, sessionGUID, LMSAvailable } = session
        console.warn('App unload triggered. Exiting app...')
        try {
            LMSAvailable && LMS.concedeControl()
            LRS.sendTerminateBeacon(user, userProfile['module-registration-id'], sessionGUID)
        }
        catch (error) {
            console.log(error)
        }
    }, [session])

    const handleCriticalError = useCallback((error: any) => {
        let errorMsg = 'An error has occurred.  Please contact your Learning Management System administrator. '
        isDebug && (errorMsg += error)
        setSession(prevState => {
            return {
                ...prevState,
                errorMessage: errorMsg
            }
        })
        handleUnload()
    }, [handleUnload])

    useEffect(() => {
        window.addEventListener('unload', handleUnload)
        return () => window.removeEventListener('unload', handleUnload)
    }, [handleUnload])

    useEffect(() => {
        if (!scriptsLoaded && !session.errorMessage) {
            isDebug && console.log('Load script effect.')

            loadScripts()
                .then(() => setScriptsLoaded(true))
                .catch((error) => handleCriticalError(error))
        }
    }, [handleCriticalError, scriptsLoaded, session.errorMessage])

    useEffect(() => {
        if (scriptsLoaded && !session.initComplete && !session.errorMessage) {
            isDebug && console.log('Init effect.')
            const init = async (sessionGUID: string) => {
                const isLMSAvailable = LMS.init()
                const user = await getUser()
                const profile = JSON.parse(await LRS.getUserProfile(user)).profile as UserModuleProfile
                await LRS.sendInitialize(user, profile['module-registration-id'], sessionGUID)
                setSession(prevState => {
                    return {
                        ...prevState,
                        user: user,
                        userProfile: profile,
                        LMSAvailable: isLMSAvailable,
                        initComplete: true
                    }
                })
            }
            init(session.sessionGUID)
                .catch((error) => handleCriticalError(error))
        }
    }, [handleCriticalError, session.initComplete, scriptsLoaded, session.errorMessage, session.sessionGUID, session, handleUnload])

    useEffect(() => {
        if (session.initComplete && !reportingData && !session.errorMessage) {
            isDebug && console.log('Data Context effect.')
            const fetchData = async () => {
                const students: Student[] = await LRS.getAllStudents(session.user.companyId)
                const profiles = await LRS.getAllModsForAllStudents(session.user.companyId)
                const simProfiles: TSModuleProfile[] = profiles.filter(isSim)
                const labProfiles: LLModuleProfile[] = profiles.filter(isLab)

                setReportingData({
                    allStudents: students,
                    allProfiles: profiles,
                    simProfiles: simProfiles,
                    labProfiles: labProfiles
                })
            }
            fetchData()
                .catch((error) => handleCriticalError(error))
        }
    }, [session.user.companyId, reportingData, session.initComplete, handleCriticalError, session.errorMessage])

    if (session.errorMessage) return <CriticalError message={session.errorMessage} />

    return (
        <ThemeProvider theme={Theme}>
            <CssBaseline />
            <GlobalStyle />
            <LoadingOverlay
                loading={!reportingData}
                text={'Loading...'}
            />
            {
                reportingData && (
                    <SessionProvider value={session}>
                        <DataContextProvider value={reportingData}>
                            <Main />
                        </DataContextProvider>
                    </SessionProvider>
                )
            }
        </ThemeProvider>
    )

}

export default App